/**
 * Currency Helpers
 * -----------------------------------------------------------------------------
 * A collection of useful functions that help with currency formatting
 *
 * Current contents
 * - formatMoney - Takes an amount in cents and returns it as a formatted dollar value.
 *
 */

import { type CurrencyCode } from "@shopify/hydrogen-react/storefront-api-types";

/**
 * Format money values based on your shop currency settings
 * @param  {Number|string} cents - value in cents or dollar amount e.g. 300 cents
 * or 3.00 dollars
 * @param  {String} format - shop money_format setting
 * @return {String} value - formatted value
 */

export type Money = string | number;

export function formatMoney(
  cents: Money,
  // eslint-disable-next-line no-template-curly-in-string
  format = "${{ amount }}",
  currencySuffix: CurrencyCode | false | undefined = undefined
) {
  if (typeof cents === "string") {
    cents = parseInt(cents.replace(".", ""));
  }

  let value = "";
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

  function formatWithDelimiters(
    number: number,
    precision: number,
    thousands?: string,
    decimal?: string
  ): string {
    precision = precision || 2;
    thousands = thousands || ",";
    decimal = decimal || ".";

    if (isNaN(number) || number === null) {
      return "0";
    }

    const stringNumber = (number / 100.0).toFixed(precision);

    const parts = stringNumber.split(".");
    const dollarsAmount = parts[0].replace(
      /(\d)(?=(\d\d\d)+(?!\d))/g,
      "$1" + thousands
    );
    const centsAmount = parts[1] ? decimal + parts[1] : "";

    if (precision === 0) {
      return dollarsAmount;
    } else {
      return dollarsAmount + centsAmount;
    }
  }

  switch (format.match(placeholderRegex)?.[1]) {
    case "amount":
      value = formatWithDelimiters(cents, 2);
      break;
    case "amount_no_decimals":
      value = formatWithDelimiters(cents, 0);
      break;
    case "amount_with_space_separator":
      value = formatWithDelimiters(cents, 2, " ", ".");
      break;
    case "amount_no_decimals_with_comma_separator":
      value = formatWithDelimiters(cents, 0, ",", ".");
      break;
    case "amount_no_decimals_with_space_separator":
      value = formatWithDelimiters(cents, 0, " ");
      break;
    case "amount_no_trailing_zeroes":
      value = formatWithDelimiters(cents, 2).replace(".00", "");
      break;
  }

  if (currencySuffix) {
    value = `${value} ${currencySuffix}`;
  }

  const formattedValue = format.replace(placeholderRegex, value);
  return formattedValue.replace(/^[$%]/g, currencySymbol());
}

export function currencySymbol() {
  if (window.Shopify && window.Shopify?.currency?.active === "GBP") {
    return "£";
  } else if (window.Shopify && window.Shopify?.currency?.active === "EUR") {
    return "€";
  } else {
    return "$";
  }
}
